import React from 'react'
import { XMarkIcon } from '@heroicons/react/16/solid'
import { Dialog, DialogActions, DialogBody, DialogTitle } from './Dialog'
import { Button } from './Button'

/** Standard popup for a table's filter fields — opened from the toolbar's FiltersButton. */
export function FiltersDialog({
    open,
    onClose,
    onReset,
    children,
}: {
    open: boolean
    onClose: () => void
    onReset: () => void
    children: React.ReactNode
}) {
    return (
        <Dialog open={open} onClose={() => onClose()} size="lg">
            <DialogTitle>Filters</DialogTitle>
            <DialogBody>
                <div className="grid gap-4 sm:grid-cols-2 [&_[data-slot=text]]:mb-1.5">{children}</div>
            </DialogBody>
            <DialogActions>
                <Button plain onClick={onReset}>
                    Reset
                </Button>
                <Button onClick={onClose}>Apply Filters</Button>
            </DialogActions>
        </Dialog>
    )
}

export interface FilterPill {
    key: string
    label: string
    onClear: () => void
}

/** Row of active-filter pills shown under the toolbar once filters are applied. Each pill clears itself; "Clear all" appears once 2+ are active. */
export function FilterPills({ pills, onClearAll }: { pills: FilterPill[]; onClearAll: () => void }) {
    if (pills.length === 0) return null

    return (
        <div className="mb-4 flex flex-wrap items-center gap-2">
            {pills.map((pill) => (
                <span
                    key={pill.key}
                    className="inline-flex items-center gap-1.5 rounded-full bg-accent px-3 py-1 text-xs font-medium text-foreground"
                >
                    {pill.label}
                    <button
                        type="button"
                        onClick={pill.onClear}
                        aria-label={`Remove ${pill.label} filter`}
                        className="rounded-full p-0.5 data-[hover]:bg-primary/10 hover:bg-primary/10"
                    >
                        <XMarkIcon className="size-3" />
                    </button>
                </span>
            ))}
            {pills.length > 1 && (
                <button
                    type="button"
                    onClick={onClearAll}
                    className="text-xs font-medium text-muted-foreground underline hover:text-foreground"
                >
                    Clear all
                </button>
            )}
        </div>
    )
}
